import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getCombinedPayoffCandidates, getCombinedPayoffLineItems } from 'services'

/** A related loan as the picker lists it. */
export interface CombinedPayoffCandidate {
  loanNumber: number
  displayNo: string
  servicerLoanNumber: string
  subjectPropertyAddress: string
  borrowerName: string
  entityTitle: string
  servicingStatus: string
  maturityDate: string
  /** Why this loan is related: same property, same entity, same borrower. */
  matchReasons: ('property' | 'entity' | 'borrower')[]
}

/** One loan's figures inside a combined quote. */
interface CombinedPayoffLoan {
  loanNumber: number
  displayNo: string
  subjectPropertyAddress: string
  total: number
}

const money = (n: any) =>
  `${Number(n) < 0 ? '-' : ''}$${Math.abs(Number(n) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const MATCH_LABEL: Record<string, string> = {
  property: 'Same property',
  entity: 'Same entity',
  borrower: 'Same borrower',
}

/**
 * Pick the related loans a payoff covers, and show what each of them takes to retire.
 *
 * A borrower paying off through one closing pays one wire, so the demand has to be one document.
 * The loans offered are the ones the Related Loans matcher already ties to this file - same subject
 * property, same entity, same borrower - and the backend refuses any other, so what is listed here
 * is exactly what can be combined.
 *
 * Each related loan is quoted by the ordinary payoff calculator against the same good-through date,
 * which is why the figures below are read-only: they are what that loan's own Payoff screen would
 * quote. The line items above belong to THIS loan and are the ones the servicer can edit.
 */
export default function CombinedPayoffPanel({
  payoffDate,
  thisLoanDisplayNo,
  thisLoanTotal,
  selected,
  onSelectedChange,
}: {
  payoffDate: string
  thisLoanDisplayNo: string
  /** This loan's total as the line items above currently stand, edits included. */
  thisLoanTotal: number
  selected: number[]
  onSelectedChange: (loanNumbers: number[]) => void
}) {
  const [candidates, setCandidates] = useState<CombinedPayoffCandidate[]>([])
  const [loaded, setLoaded] = useState(false)
  const [quoting, setQuoting] = useState(false)
  const [quoted, setQuoted] = useState<CombinedPayoffLoan[]>([])

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getCombinedPayoffCandidates()
        setCandidates(res?.candidates || [])
      } catch {
        // An unreadable candidate list leaves the panel saying there is nothing to combine, which
        // is the safe reading: it never silently narrows a demand that was already being built.
        setCandidates([])
      } finally {
        setLoaded(true)
      }
    }
    load()
  }, [])

  // Quote the chosen loans whenever the choice or the date moves. Only the related loans are read
  // from the response: this loan's own figure is the one on screen above, edits and all, and that
  // is also the one the generated statement uses.
  useEffect(() => {
    const quote = async () => {
      if (!payoffDate || !selected.length) {
        setQuoted([])
        return
      }
      setQuoting(true)
      try {
        const res = await getCombinedPayoffLineItems({ payoffDate, loanNumbers: selected })
        const loans: CombinedPayoffLoan[] = res?.loans || []
        setQuoted(loans.filter((loan) => selected.includes(loan.loanNumber)))
      } catch (error: any) {
        setQuoted([])
        toast(error?.message || 'Could not quote the selected loans.', { type: 'error' })
      } finally {
        setQuoting(false)
      }
    }
    quote()
  }, [payoffDate, selected])

  const toggle = (loanNumber: number) => {
    onSelectedChange(
      selected.includes(loanNumber) ? selected.filter((no) => no !== loanNumber) : [...selected, loanNumber],
    )
  }

  const relatedTotal = quoted.reduce((sum, loan) => sum + (Number(loan.total) || 0), 0)

  if (!loaded)
    return (
      <div className="mb-4 rounded border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
        Looking for related loans…
      </div>
    )

  if (!candidates.length)
    return (
      <div className="mb-4 rounded border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
        No other open loan shares this loan&apos;s property, entity or borrower, so there is nothing to combine with
        it. A loan already paid in full is never offered.
      </div>
    )

  return (
    <div className="mb-4 rounded border border-gray-200 bg-gray-50 px-4 py-3">
      <div className="text-sm text-gray-700">
        Choose the loans this demand covers. Each one is quoted against the same Good through Date, and the statement
        prints the schedule of what every loan takes to retire plus the single total to wire.
      </div>
      <div className="mt-3 space-y-2">
        {candidates.map((candidate) => {
          const isOn = selected.includes(candidate.loanNumber)
          const quote = quoted.find((loan) => loan.loanNumber === candidate.loanNumber)
          return (
            <label
              key={candidate.loanNumber}
              className={`flex items-start gap-3 rounded border px-3 py-2 cursor-pointer transition ${
                isOn ? 'border-shade-blue bg-white' : 'border-gray-200 bg-white hover:border-shade-blue/50'
              }`}
            >
              <input type="checkbox" className="mt-1" checked={isOn} onChange={() => toggle(candidate.loanNumber)} />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-gray-900">Loan {candidate.displayNo}</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {isOn ? (quote ? money(quote.total) : quoting ? 'Quoting…' : '-') : ''}
                  </span>
                </div>
                <div className="text-xs text-gray-600">
                  {[candidate.entityTitle || candidate.borrowerName, candidate.subjectPropertyAddress]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {candidate.matchReasons.map((reason) => (
                    <span
                      key={reason}
                      className="text-[11px] px-1.5 py-0.5 rounded-full bg-shade-blue/10 text-shade-blue"
                    >
                      {MATCH_LABEL[reason] || reason}
                    </span>
                  ))}
                  {candidate.servicingStatus && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {candidate.servicingStatus}
                    </span>
                  )}
                  {/* A loan with no maturity date cannot be quoted at all, and the demand refuses
                      rather than printing a blank one - so say it here instead of at the click. */}
                  {!candidate.maturityDate && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700">
                      No maturity date
                    </span>
                  )}
                </div>
              </div>
            </label>
          )
        })}
      </div>

      {selected.length > 0 && (
        <div className="mt-3 border-t border-gray-200 pt-3 text-sm">
          <div className="flex items-center justify-between text-gray-700">
            <span>Loan {thisLoanDisplayNo} (the line items below)</span>
            <span className="font-semibold">{money(thisLoanTotal)}</span>
          </div>
          <div className="flex items-center justify-between text-gray-700 mt-1">
            <span>
              {selected.length} related loan{selected.length === 1 ? '' : 's'}
            </span>
            <span className="font-semibold">{quoting ? 'Quoting…' : money(relatedTotal)}</span>
          </div>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200 text-gray-900">
            <span className="font-semibold">Total due to pay off all loans</span>
            <span className="font-semibold text-lg">{quoting ? '-' : money(thisLoanTotal + relatedTotal)}</span>
          </div>
          <p className="mt-2 text-[11px] text-gray-500">
            The payoff processing fees are quoted once, on this loan, because they pay for issuing one demand. Every
            loan covered keeps the statement in its own payoff log and documents, each carrying the figures quoted for
            it, so approving a loan&apos;s row books that loan&apos;s own fees.
          </p>
        </div>
      )}
    </div>
  )
}
